import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import './index.css';

const MemberLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changeEmail, setChangeEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const role = userSnap.data().role;
            if (role === 'member') {
              navigate('/member-dashboard', { replace: true, state: { memberId: user.uid } });
            } else {
              setError('You are not authorized to access the member dashboard.');
              await auth.signOut();
            }
          } else {
            setError('User data not found. Please contact admin.');
            await auth.signOut();
          }
        } catch (error) {
          setError('An error occurred while checking your role.');
          await auth.signOut();
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, auth, db]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setError('User data not found. Please contact admin.');
        await auth.signOut();
        return;
      }

      const role = userSnap.data().role;
      if (role !== 'member') {
        setError('Access denied. Only members can log in here.');
        await auth.signOut();
        return;
      }

      navigate('/member-dashboard', { replace: true, state: { memberId: user.uid } });
    } catch (err) {
      let errorMessage = 'Invalid email or password.';
      switch (err.code) {
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid credentials. Please check your email and password.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Please try again later.';
          break;
        default:
          errorMessage = 'An error occurred. Please try again.';
      }
      setError(errorMessage);
      setEmail('');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setChangeError('');
    setChangeSuccess('');

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user is currently signed in.');
      }
      const credential = EmailAuthProvider.credential(changeEmail, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setChangeSuccess('Password updated successfully!');
      setChangeEmail('');
      setCurrentPassword('');
      setNewPassword('');
      setShowChangePassword(false);
    } catch (error) {
      let errorMessage = 'Failed to change password. Please try again.';
      switch (error.code) {
        case 'auth/wrong-password':
          errorMessage = 'Current password is incorrect.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        case 'auth/user-mismatch':
          errorMessage = 'Email does not match the current user.';
          break;
        case 'auth/weak-password':
          errorMessage = 'New password is too weak. Use at least 6 characters.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many attempts. Please try again later.';
          break;
        default:
          errorMessage = 'An error occurred. Please try again.';
      }
      setChangeError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="member-login-container">
      <div className="member-login-card">
        <h2>{showChangePassword ? 'Change Password' : 'Member Login'}</h2>
        {error && <div className="error-message">{error}</div>}
        {changeSuccess && <div className="success-message">{changeSuccess}</div>}
        {changeError && <div className="error-message">{changeError}</div>}
        {!showChangePassword ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="password">Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                disabled={loading}
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Login'}
            </button>
            <div className="text-center mt-4">
              <span
                className="text-link"
                onClick={() => {
                  setShowChangePassword(true);
                  setError('');
                  setChangeSuccess('');
                  setChangeError('');
                }}
              >
                Change Password
              </span>
            </div>
          </form>
        ) : (
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label htmlFor="changeEmail">Email Address</label>
              <input
                type="email"
                id="changeEmail"
                value={changeEmail}
                onChange={(e) => setChangeEmail(e.target.value)}
                required
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>
            <div className="form-group password-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Enter current password"
                disabled={loading}
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <div className="form-group password-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
                disabled={loading}
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </span>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? <span className="spinner"></span> : 'Update Password'}
            </button>
            <div className="text-center mt-4">
              <span
                className="text-link"
                onClick={() => {
                  setShowChangePassword(false);
                  setChangeEmail('');
                  setCurrentPassword('');
                  setNewPassword('');
                  setChangeError('');
                  setChangeSuccess('');
                }}
              >
                Back to Login
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MemberLogin;